import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  X,
  MoreHorizontal,
  Pencil,
  Trash2,
  Database,
  Check,
  XCircle,
  FileText,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PageHeader, MetricCard, DataTable, Column, KPIStrip } from '@/components/common';
import {
  chargeTypesApi,
  ChargeType,
  ChargeCategoryExtended,
  RateUnit,
  CreateChargeTypeDto,
  QueryChargeTypeParams,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { stagger, fadeUp } from '@/lib/animations';

// Constants
const CHARGE_CATEGORIES: { value: ChargeCategoryExtended; label: string }[] = [
  { value: 'Freight', label: 'Freight' },
  { value: 'OriginCharges', label: 'Origin Charges' },
  { value: 'DestinationCharges', label: 'Destination Charges' },
  { value: 'OriginHaulage', label: 'Origin Haulage' },
  { value: 'DestinationHaulage', label: 'Destination Haulage' },
  { value: 'ExportClearance', label: 'Export Clearance' },
  { value: 'ImportClearance', label: 'Import Clearance' },
  { value: 'Documentation', label: 'Documentation' },
  { value: 'Insurance', label: 'Insurance' },
];

const RATE_UNITS: { value: RateUnit; label: string }[] = [
  { value: 'PerContainer', label: 'Per Container' },
  { value: 'PerCBM', label: 'Per CBM' },
  { value: 'PerKG', label: 'Per KG' },
  { value: 'PerMile', label: 'Per Mile' },
  { value: 'PerCWT', label: 'Per CWT' },
  { value: 'PerTon', label: 'Per Ton' },
  { value: 'PerPallet', label: 'Per Pallet' },
  { value: 'PerUnit', label: 'Per Unit' },
  { value: 'PerShipment', label: 'Per Shipment' },
  { value: 'Flat', label: 'Flat' },
  { value: 'Percentage', label: 'Percentage' },
];

const CATEGORY_COLORS: Record<ChargeCategoryExtended, string> = {
  Freight: 'bg-primary-50 text-primary-600',
  OriginCharges: 'bg-success-50 text-success-600',
  DestinationCharges: 'bg-purple-50 text-purple-600',
  OriginHaulage: 'bg-warning-50 text-warning-600',
  DestinationHaulage: 'bg-warning-100 text-warning-700',
  ExportClearance: 'bg-primary-100 text-primary-700',
  ImportClearance: 'bg-success-100 text-success-700',
  Documentation: 'bg-muted text-muted-foreground',
  Insurance: 'bg-danger-50 text-danger-500',
};

export default function ChargeTypes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [chargeTypes, setChargeTypes] = useState<ChargeType[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  // Sheet/Dialog states
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingChargeType, setEditingChargeType] = useState<ChargeType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chargeToDelete, setChargeToDelete] = useState<ChargeType | null>(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateChargeTypeDto>({
    code: '',
    name: '',
    category: 'Freight',
    description: '',
    rateUnit: 'Flat',
    isForRFQ: true,
  });

  // Parse filters from URL
  const filters: QueryChargeTypeParams = useMemo(
    () => ({
      search: searchParams.get('search') || undefined,
      category: (searchParams.get('category') as ChargeCategoryExtended) || undefined,
      isActive:
        searchParams.get('isActive') === 'true'
          ? true
          : searchParams.get('isActive') === 'false'
            ? false
            : undefined,
      page: parseInt(searchParams.get('page') || '1'),
      limit: parseInt(searchParams.get('limit') || '50'),
    }),
    [searchParams]
  );

  // Set filter helper
  const setFilter = (key: keyof QueryChargeTypeParams, value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    if (key !== 'page') {
      params.set('page', '1');
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = filters.search || filters.category || filters.isActive !== undefined;

  // Load data
  const loadChargeTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await chargeTypesApi.getAll(filters);
      setChargeTypes(response.data);
      setTotalCount(response.meta?.total ?? response.data.length);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadChargeTypes();
  }, [loadChargeTypes]);

  // Computed stats
  const activeCount = useMemo(() => chargeTypes.filter((c) => c.isActive).length, [chargeTypes]);
  const rfqEnabledCount = useMemo(
    () => chargeTypes.filter((c) => c.isForRFQ).length,
    [chargeTypes]
  );
  const categoryBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    chargeTypes.forEach((c) => {
      counts[c.category] = (counts[c.category] || 0) + 1;
    });
    return counts;
  }, [chargeTypes]);

  // Handlers
  const handleCreate = () => {
    setEditingChargeType(null);
    setFormData({
      code: '',
      name: '',
      category: 'Freight',
      description: '',
      rateUnit: 'Flat',
      isForRFQ: true,
    });
    setSheetOpen(true);
  };

  const handleEdit = (chargeType: ChargeType) => {
    setEditingChargeType(chargeType);
    setFormData({
      code: chargeType.code,
      name: chargeType.name,
      category: chargeType.category,
      description: chargeType.description || '',
      rateUnit: chargeType.rateUnit,
      isForRFQ: chargeType.isForRFQ,
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingChargeType) {
        await chargeTypesApi.update(editingChargeType.id, formData);
        toast.success('Charge type updated successfully');
      } else {
        await chargeTypesApi.create(formData);
        toast.success('Charge type created successfully');
      }
      setSheetOpen(false);
      await loadChargeTypes();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!chargeToDelete) return;

    try {
      await chargeTypesApi.delete(chargeToDelete.id);
      toast.success('Charge type deleted successfully');
      setDeleteDialogOpen(false);
      setChargeToDelete(null);
      await loadChargeTypes();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleActive = async (chargeType: ChargeType) => {
    try {
      await chargeTypesApi.toggleActive(chargeType.id, chargeType.isActive);
      toast.success(`Charge type ${chargeType.isActive ? 'deactivated' : 'activated'}`);
      await loadChargeTypes();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const result = await chargeTypesApi.seedDefaults();
      toast.success(`${result.created} default charge types created`);
      await loadChargeTypes();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSeeding(false);
    }
  };

  const chargeTypeColumns: Column<ChargeType>[] = [
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      render: (row) => <span className="font-mono text-sm font-medium">{row.code}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => (
        <div>
          <div className="font-medium">{row.name}</div>
          {row.description && (
            <div className="text-sm text-muted-foreground truncate max-w-xs">{row.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className={CATEGORY_COLORS[row.category]}>
          {row.category.replace(/([A-Z])/g, ' $1').trim()}
        </Badge>
      ),
    },
    {
      key: 'rateUnit',
      header: 'Rate Unit',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.rateUnit
            ? RATE_UNITS.find((u) => u.value === row.rateUnit)?.label || row.rateUnit
            : '-'}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      sortable: true,
      render: (row) =>
        row.isActive ? (
          <Badge className="bg-success-50 text-success-600">Active</Badge>
        ) : (
          <Badge className="bg-muted text-muted-foreground">Inactive</Badge>
        ),
    },
    {
      key: 'isForRFQ',
      header: 'RFQ',
      sortable: true,
      // Spelled out rather than a ✓/✗ glyph: the icon was the only content of
      // this cell, so it had to become text to survive the icon cleanup.
      render: (row) =>
        row.isForRFQ ? (
          <span className="text-sm">Yes</span>
        ) : (
          <span className="text-sm text-muted-foreground">No</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'w-10',
      render: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleEdit(row)}>
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleToggleActive(row)}>
              {row.isActive ? (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Deactivate
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Activate
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => {
                setChargeToDelete(row);
                setDeleteDialogOpen(true);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Charge Types"
          description="Cost components available to freight rates and quotations, with the unit each is priced in."
          actions={
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleSeedDefaults} disabled={seeding}>
                <Database className="w-4 h-4 mr-2" />
                {seeding ? 'Seeding...' : 'Seed Defaults'}
              </Button>
              <Button onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-2" />
                New Charge Type
              </Button>
            </div>
          }
        />
      </motion.div>

      {/* KPIs */}
      <div>
        <KPIStrip columns={4}>
          <MetricCard
            label="Total Charge Types"
            value={totalCount}
            sentiment="neutral"
            icon={<FileText className="w-5 h-5" />}
          />
          <MetricCard
            label="Active"
            value={activeCount}
            sentiment="success"
            icon={<Check className="w-5 h-5 text-success-500" />}
          />
          <MetricCard
            label="RFQ Enabled"
            value={rfqEnabledCount}
            sentiment="info"
            icon={<FileText className="w-5 h-5 text-primary-500" />}
          />
          <MetricCard
            label="Categories"
            value={Object.keys(categoryBreakdown).length}
            sentiment="neutral"
            icon={<Database className="w-5 h-5 text-purple-500" />}
          />
        </KPIStrip>
      </div>

      {/* Filters */}
      <motion.div variants={fadeUp}>
        <div className={cn(PANEL, 'p-4')}>
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by code or name..."
                value={filters.search || ''}
                onChange={(e) => setFilter('search', e.target.value || undefined)}
                className="pl-10 pr-10"
              />
              {filters.search && (
                <button
                  onClick={() => setFilter('search', undefined)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Category Filter */}
            <Select
              value={filters.category || 'ALL_CATEGORIES'}
              onValueChange={(v) => setFilter('category', v === 'ALL_CATEGORIES' ? undefined : v)}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL_CATEGORIES">All Categories</SelectItem>
                {CHARGE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Active Filter */}
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={filters.isActive === true}
                onCheckedChange={(checked) => setFilter('isActive', checked ? 'true' : undefined)}
              />
              Active Only
            </label>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-destructive hover:text-destructive"
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Table */}
      <motion.div variants={fadeUp}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <DataTable
            data={chargeTypes}
            columns={chargeTypeColumns}
            pagination
            emptyTitle="No Charge Types Found"
            emptyDescription={
              hasActiveFilters
                ? 'No charge types match your filters.'
                : 'Get started by creating a charge type or seeding defaults.'
            }
          />
        )}
      </motion.div>

      {/* Create/Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{editingChargeType ? 'Edit Charge Type' : 'New Charge Type'}</SheetTitle>
            <SheetDescription>
              {editingChargeType
                ? 'Update the charge type details below.'
                : 'Create a new charge type for rate management.'}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 py-6">
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., THC, BAF, DOC"
                disabled={!!editingChargeType}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Terminal Handling Charge"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(v) =>
                  setFormData({ ...formData, category: v as ChargeCategoryExtended })
                }
              >
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHARGE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rateUnit">Rate Unit</Label>
              <Select
                value={formData.rateUnit || ''}
                onValueChange={(v) => setFormData({ ...formData, rateUnit: v as RateUnit })}
              >
                <SelectTrigger id="rateUnit">
                  <SelectValue placeholder="Select unit..." />
                </SelectTrigger>
                <SelectContent>
                  {RATE_UNITS.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="isForRFQ"
                checked={formData.isForRFQ}
                onCheckedChange={(checked) => setFormData({ ...formData, isForRFQ: !!checked })}
              />
              <Label htmlFor="isForRFQ" className="font-normal">
                Available for RFQ submissions
              </Label>
            </div>
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingChargeType ? 'Update' : 'Create'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-danger-50 rounded-full">
                <Trash2 className="w-5 h-5 text-danger-500" />
              </div>
              Delete Charge Type
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium">{chargeToDelete?.code}</span>? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.main>
  );
}
